// (B) 플레인 Descriptions — antd Descriptions 대체(순수 시각/구조). antd DOM 미러링:
// .descriptions > .descriptions-header / .descriptions-view > table > tbody > tr.descriptions-row > ...
//   bordered  : th.item-label + td.item-content(각각 별도 셀, 내부 span). antd bordered 파리티.
//   비-bordered: td.item > div.item-container(flex) > span.item-label + span.item-content. antd 기본 파리티.
//   (antd 는 bordered 유무로 이 두 구조를 분기 렌더한다. 소비처 SCSS 는 각 구조에 맞춰 작성됨.)
//
// ⚠ ninegqlUI(ngql 데이터레이어 UI)가 batch9 까지 antd Descriptions 를 직접 렌더하고, 그 시각을
// 전역 .om-descriptions* SCSS 에 의존한다. 따라서 여기선 antd 클래스명을 그대로 유지하되 .om-descriptions*
// 를 함께 부여(이중 클래스 병존): 기존 .om-descriptions* SCSS 가 antd(ninegqlUI)·Plain 양쪽을 스타일하고,
// .om-descriptions* 는 batch9(ninegqlUI 이관 시 일괄 rename) hook. 소비처 SCSS 무변경.
import { Children, isValidElement } from 'react'
import type { ReactElement, ReactNode } from 'react'
import type { OmwDescriptionsItemProps, OmwDescriptionsProps } from '../api/OmwDescriptions'
import { cx } from './cx'
import '../styles/omwDescriptions.css'

// om-descriptions-* 클래스 생성 헬퍼.
const dc = (suffix: string) => `om-descriptions-${suffix}`

interface ItemData {
  label?: ReactNode
  content: ReactNode
  className?: string
}

function chunk<T>(items: T[], size: number): T[][] {
  const rows: T[][] = []
  for (let i = 0; i < items.length; i += size) rows.push(items.slice(i, i + size))
  return rows
}

export function PlainDescriptions({
  size,
  column = 3,
  bordered,
  title,
  extra,
  className,
  style,
  children,
}: OmwDescriptionsProps) {
  const items: ItemData[] = Children.toArray(children)
    .filter((child): child is ReactElement<OmwDescriptionsItemProps> => isValidElement(child))
    .map((child) => ({
      label: child.props.label,
      content: child.props.children,
      className: child.props.className,
    }))

  const rows = chunk(items, Math.max(1, column))
  const sizeClass = size === 'small' ? 'om-descriptions-small' : undefined
  const borderedClass = bordered === true ? 'om-descriptions-bordered' : undefined

  return (
    <div className={cx('om-descriptions', sizeClass, borderedClass, className)} style={style}>
      {(title != null || extra != null) && (
        <div className={dc('header')}>
          {title != null && <div className={dc('title')}>{title}</div>}
          {extra != null && <div className={dc('extra')}>{extra}</div>}
        </div>
      )}
      <div className={dc('view')}>
        <table>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr key={rowIdx} className={dc('row')}>
                {row.map((item, itemIdx) => {
                  // 마지막 행이 덜 찼으면 마지막 셀이 남은 열 폭을 채움(antd colSpan).
                  // bordered 는 item 당 셀 2개(label+content), 비-bordered 는 1개(item) → 배수가 다름.
                  const isLast = itemIdx === row.length - 1
                  const remaining = column - row.length
                  const colSpan = isLast
                    ? bordered === true
                      ? remaining * 2 + 1
                      : remaining + 1
                    : 1
                  return (
                    <ItemCells
                      key={itemIdx}
                      item={item}
                      colSpan={colSpan}
                      bordered={bordered === true}
                    />
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ItemCells({
  item,
  colSpan,
  bordered,
}: {
  item: ItemData
  colSpan: number
  bordered: boolean
}) {
  const hasLabel = item.label !== undefined
  // label 텍스트가 없으면(null·빈 문자열) label 노드를 렌더하지 않는다 — labelKey=null(라벨 미표시)
  // 항목에서 소비처 콜론(::after)이 빈 라벨에 남는 것을 방지.
  const labelText = item.label != null && item.label !== '' ? item.label : null

  // 비-bordered: td.item > div.item-container(flex) > span.item-label + span.item-content (antd 기본 파리티).
  if (!bordered) {
    return (
      <td className={cx('om-descriptions-item', item.className)} colSpan={colSpan}>
        <div className={dc('item-container')}>
          {hasLabel && <span className={dc('item-label')}>{labelText}</span>}
          <span className={dc('item-content')}>{item.content}</span>
        </div>
      </td>
    )
  }

  // bordered: th.item-label + td.item-content 별도 셀. label/content 를 plain <span> 으로 감싼다
  // (소비처 SCSS 가 `.item-content > span` wrapper 를 셀렉터로 씀). th 는 label 없어도 유지해 열 정렬 보존.
  // item.className 은 label·content 양쪽 셀에 부여(antd Row: bordered 시 label/content Cell 모두 className 수신).
  return (
    <>
      {hasLabel && (
        <th className={cx('om-descriptions-item-label', item.className)}>
          {labelText != null && <span>{labelText}</span>}
        </th>
      )}
      <td className={cx('om-descriptions-item-content', item.className)} colSpan={colSpan}>
        <span>{item.content}</span>
      </td>
    </>
  )
}

// OmDescriptionsItem 은 데이터 홀더(PlainDescriptions 가 props 를 직접 읽음). 렌더 시 no-op.
export function PlainDescriptionsItem(_props: OmwDescriptionsItemProps) {
  return null
}
